import React, { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';
import { getRandomCardImage } from '@/utils/cardImage';

import { useCreateClientMutation } from '../../../graphql/mutations/generated/createClient';
import { useUpdateClientMutation } from '../../../graphql/mutations/generated/updateClient';
import { CreateClientFormValueType, CreateClientModalProps } from '../types';
import {
  adaptFormValuesToCreateClientInput,
  adaptFormValuesToUpdateClientInput,
  getCreateClientFormFields,
  getCreateClientFormInitialValues,
  validateCreateClientForm,
} from '../utils';

export default function useCreateClient(props: CreateClientModalProps) {
  const { initialFormValues, clientId, onClose, onSuccess } = props;
  const { t } = useTranslation();
  const initialValues = useMemo<CreateClientFormValueType>(
    () => ({
      ...getCreateClientFormInitialValues(),
      ...initialFormValues,
    }),
    [initialFormValues],
  );
  const { handleResponse } = useResponse();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { showSnackbar } = useSnackbar();
  const [{ fetching: isCreating }, createClient] = useCreateClientMutation();
  const [{ fetching: isUpdating }, updateClient] = useUpdateClientMutation();
  const [submitRef, setSubmitRef] = useState<() => void>(() => {});
  const defaultCover = useMemo(() => getRandomCardImage('EVENT'), []);

  const onCreate = React.useCallback(
    async (values: CreateClientFormValueType) => {
      const res = await createClient({
        data: adaptFormValuesToCreateClientInput(values),
      });
      handleResponse({
        condition: !!res.data?.createClient?.client?.id,
        onSuccess: () => {
          showSnackbar({
            message: `Client ${res.data?.createClient?.client?.clientName} created.`,
          });
          onSuccess?.(res.data?.createClient?.client!);
          onClose();
        },
        onError: () => {
          showDefaultErrorSnackbar();
        },
      });
    },
    [createClient, handleResponse, onClose, onSuccess, showDefaultErrorSnackbar, showSnackbar],
  );

  const onUpdate = React.useCallback(
    async (values: CreateClientFormValueType) => {
      if (!clientId) {
        return;
      }
      const res = await updateClient({
        data: adaptFormValuesToUpdateClientInput(values, clientId),
      });
      handleResponse({
        condition: !!res.data?.updateClient?.client?.id,
        onSuccess: () => {
          showSnackbar({
            message: `Client ${res.data?.updateClient?.client?.clientName} updated.`,
          });
          onClose();
        },
        onError: () => {
          showDefaultErrorSnackbar();
        },
      });
    },
    [clientId, handleResponse, onClose, showDefaultErrorSnackbar, showSnackbar, updateClient],
  );

  const isEditing = Boolean(clientId);

  const fields = useMemo(
    () => getCreateClientFormFields({ t, defaultCover, isEditing }),
    [defaultCover, isEditing, t],
  );

  const validate = useMemo(
    () => (values: CreateClientFormValueType) => validateCreateClientForm(t, values),
    [t],
  );

  return {
    fields,
    validate,
    onSubmit: clientId ? onUpdate : onCreate,
    onClose,
    submitRef,
    setSubmitRef,
    fetching: isCreating || isUpdating,
    initialValues,
    isEditing,
  };
}
